import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const WA_NUMBER = '971581735200';

const SERVICES = [
    'Marble Polishing',
    'Tile & Marble Installation',
    'Ceiling Works',
    'Joinery & Carpentry',
    'General Enquiry',
];

const buildWaLink = (name, service, message) => {
    const text = [
        `Hello Royal Standard! 👋`,
        name ? `My name is ${name}.` : '',
        service ? `I'm interested in: *${service}*.` : '',
        message ? `\n${message}` : '',
        '\nLooking forward to hearing from you.',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
};

const Contact = () => {
    const [name, setName] = useState('');
    const [service, setService] = useState('');
    const [message, setMessage] = useState('');

    return (
        <section id="contact" className="relative py-24 bg-gray-50 overflow-hidden">

            {/* subtle gold radial glow */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.06) 0%, transparent 70%)' }}
            />

            <div className="max-w-6xl mx-auto px-6 relative z-10">

                {/* ── Header ── */}
                <div className="text-center mb-16">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="uppercase tracking-[0.3em] text-xs font-semibold mb-3"
                        style={{ color: '#D4AF37' }}
                    >
                        Get In Touch
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                        className="font-serif text-4xl md:text-5xl text-gray-900 mb-4 leading-tight"
                    >
                        Let's Start on{' '}
                        <em className="not-italic" style={{ color: '#D4AF37' }}>WhatsApp</em>
                    </motion.h2>
                    <motion.div
                        initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.5 }}
                        className="w-14 h-px mx-auto mt-4"
                        style={{ background: '#D4AF37', transformOrigin: 'center' }}
                    />
                </div>

                {/* ── Main grid ── */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

                    {/* ── Left info column ── */}
                    <motion.div
                        initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:col-span-2 flex flex-col gap-6"
                    >
                        {/* WhatsApp hero card */}
                        <div
                            className="rounded-sm p-7 flex flex-col items-center text-center gap-4"
                            style={{
                                background: 'linear-gradient(135deg,rgba(18,140,126,0.08),rgba(37,211,102,0.08))',
                                border: '1px solid rgba(37,211,102,0.3)',
                            }}
                        >
                            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#25D366' }}>
                                <MessageCircle size={28} color="#fff" />
                            </div>
                            <div>
                                <h3 className="font-serif text-xl text-gray-900 mb-1">WhatsApp Us Directly</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">
                                    Fastest way to reach us — responses within 60 minutes.
                                </p>
                            </div>
                            <a
                                href={`https://wa.me/${WA_NUMBER}`}
                                target="_blank" rel="noreferrer"
                                className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-white text-sm font-semibold transition-all duration-300 hover:brightness-110"
                                style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}
                            >
                                <MessageCircle size={16} /> +971 58 173 5200
                            </a>
                        </div>

                        {/* Contact details */}
                        <div className="bg-white rounded-sm p-6 space-y-5 shadow-sm border border-gray-100">
                            {[
                                { icon: <Phone size={16} />, label: 'Phone', value: '+971 58 173 5200', href: 'tel:+971581735200' },
                                { icon: <Mail size={16} />, label: 'Email', value: 'info@royalstandarduae.com', href: 'mailto:info@royalstandarduae.com' },
                                { icon: <MapPin size={16} />, label: 'Location', value: 'Dubai, United Arab Emirates', href: null },
                                { icon: <Clock size={16} />, label: 'Hours', value: 'Mon – Sat: 8 AM – 7 PM', href: null },
                            ].map((c, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <div
                                        className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
                                        style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}
                                    >
                                        {c.icon}
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-[10px] uppercase tracking-widest">{c.label}</p>
                                        {c.href
                                            ? <a href={c.href} className="text-gray-700 text-sm hover:text-yellow-600 transition-colors">{c.value}</a>
                                            : <p className="text-gray-700 text-sm">{c.value}</p>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Why WhatsApp */}
                        <div className="bg-white rounded-sm p-5 space-y-3 shadow-sm border border-gray-100">
                            <p className="uppercase tracking-widest text-[10px] font-semibold mb-3" style={{ color: '#D4AF37' }}>
                                Why WhatsApp?
                            </p>
                            {[
                                'Instant reply — no hold music',
                                'Share project photos directly',
                                'Get a quote faster than email',
                                'Available in Arabic & English',
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <CheckCircle size={14} style={{ color: '#25D366' }} className="flex-shrink-0" />
                                    <span className="text-gray-500 text-xs">{item}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* ── Right: message builder ── */}
                    <motion.div
                        initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:col-span-3 bg-white rounded-sm p-8 md:p-10 shadow-sm border border-gray-100"
                    >
                        <p className="uppercase tracking-widest text-xs font-semibold mb-2" style={{ color: '#D4AF37' }}>
                            Message Builder
                        </p>
                        <h3 className="font-serif text-2xl text-gray-900 mb-1">Send via WhatsApp</h3>
                        <p className="text-gray-500 text-sm mb-7">
                            Fill in the form — your message will be pre-written and ready to send in one tap.
                        </p>

                        <div className="space-y-5">
                            {/* Name */}
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">Your Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Ahmed Al Mansoori"
                                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                                />
                            </div>

                            {/* Service */}
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">Service Required</label>
                                <select
                                    value={service}
                                    onChange={(e) => setService(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-yellow-500 transition-colors appearance-none"
                                >
                                    <option value="">Select a service…</option>
                                    {SERVICES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </div>

                            {/* Message */}
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">Project Details</label>
                                <textarea
                                    rows={4}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Describe your space, requirements or any questions…"
                                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                                />
                            </div>

                            {/* Live preview */}
                            {(name || service || message) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="rounded p-4"
                                    style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.2)' }}
                                >
                                    <p className="text-green-600 text-[10px] uppercase tracking-widest mb-2">Message Preview</p>
                                    <p className="text-gray-600 text-xs leading-relaxed whitespace-pre-line">
                                        {[
                                            `Hello Royal Standard! 👋`,
                                            name ? `My name is ${name}.` : '',
                                            service ? `I'm interested in: *${service}*.` : '',
                                            message ? `\n${message}` : '',
                                            '\nLooking forward to hearing from you.',
                                        ].filter(Boolean).join('\n')}
                                    </p>
                                </motion.div>
                            )}

                            {/* CTA */}
                            <a
                                href={buildWaLink(name, service, message)}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-3 w-full py-4 text-white text-sm uppercase tracking-widest font-semibold rounded-full transition-all hover:brightness-110"
                                style={{ background: '#25D366', boxShadow: '0 6px 28px rgba(37,211,102,0.25)' }}
                            >
                                <MessageCircle size={18} />
                                WHATSAPP US
                                <ArrowRight size={15} />
                            </a>

                            <p className="text-gray-400 text-[11px] text-center">
                                WhatsApp will open with your message pre-filled. No account required on our end.
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* ── Full page contact link ── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className="text-center mt-12"
                >
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-gray-400 text-xs uppercase tracking-widest hover:text-yellow-600 transition-colors"
                    >
                        View full contact page <ArrowRight size={13} />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;
